import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import { useExperiment } from '@nerdwallet/features';
import { setTrk } from '@nerdwallet/nw-client-lib/trk-helper';

import { Box, Type, Button } from '@nerdwallet/currency';
import { trackInteraction } from '../../../lib/analytics-helpers';
import {
  HOMEPAGE as analyticsBase,
  HOMEPAGE_SEGMENT_INSTRUMENTATION,
} from '../../../lib/analytics-constants';

import FrontPageDeEmphasizeSecondaryCtaExperiment from '../../../experiments/FrontPageDeEmphasizeSecondaryCtaExperiment';

import styles from './vertical-comparison.module.less';

import useAnalytics from '../../../lib/segment/useAnalytics';

const VerticalComparisonTab = ({
  id,
  subtitle,
  description,
  primaryButtonText,
  primaryButtonUrl,
  secondaryButtonText,
  secondaryButtonUrl,
  abTestOverrides,
}) => {
  const { assignedVariantName, loading } = useExperiment({
    testName: abTestOverrides?.testName || '',
  });
  const [primaryUrl, setPrimaryUrl] = useState();

  const primaryEntityName = `${analyticsBase.entity_names.primary_cta}_${id}`;
  const secondaryEntityName = `${analyticsBase.entity_names.secondary_cta}_${id}`;

  const { trackSegmentTrackEvent } = useAnalytics();

  useEffect(() => {
    const activeVariant = abTestOverrides?.variants.find(
      (variant) => assignedVariantName === variant.variantName
    );

    const getAbTestPrimaryUrl = () => {
      return activeVariant.variantName === 'control'
        ? activeVariant.url
        : setTrk(activeVariant);
    };

    setPrimaryUrl(activeVariant ? getAbTestPrimaryUrl() : primaryButtonUrl);
  }, [abTestOverrides, assignedVariantName, primaryButtonUrl]);

  if (loading) return null;

  return (
    <Box className={classNames(styles.backgroundImage)}>
      <Box className={classNames(styles.contentWrapper)}>
        <Box className={classNames(styles.contentBox)}>
          <Type bold className={classes('margin-bottom-3')}>
            {subtitle}
          </Type>
          <Type className={classes('margin-bottom-4')}>{description}</Type>
          <FrontPageDeEmphasizeSecondaryCtaExperiment
            primaryButtonText={primaryButtonText}
            primaryButtonUrl={primaryButtonUrl}
            primaryTrackInteraction={() => {
              trackInteraction({
                entityName: primaryEntityName,
                sectionName: analyticsBase.section_name,
                action: 'select',
              });
            }}
            secondaryButtonText={secondaryButtonText}
            secondaryButtonUrl={secondaryButtonUrl}
            secondaryTrackInteraction={() => {
              trackInteraction({
                entityName: secondaryEntityName,
                sectionName: analyticsBase.section_name,
                action: 'select',
              });
            }}
          >
            <div className={styles.buttonWidth}>
              <Button
                primary
                block
                href={primaryUrl}
                component="a"
                onClick={() => {
                  trackInteraction({
                    entityName: primaryEntityName,
                    sectionName: analyticsBase.section_name,
                    action: 'select',
                  });
                  trackSegmentTrackEvent('Element Interaction', {
                    entity_name: primaryEntityName,
                    location:
                      HOMEPAGE_SEGMENT_INSTRUMENTATION.vertical_comparison
                        .location,
                    interaction_type: 'select',
                  });
                }}
              >
                {primaryButtonText}
              </Button>
            </div>
            <div className={styles.buttonWidth}>
              <Button
                secondary
                block
                href={secondaryButtonUrl}
                component="a"
                onClick={() => {
                  trackInteraction({
                    entityName: secondaryEntityName,
                    sectionName: analyticsBase.section_name,
                    action: 'select',
                  });
                  trackSegmentTrackEvent('Element Interaction', {
                    entity_name: secondaryEntityName,
                    location:
                      HOMEPAGE_SEGMENT_INSTRUMENTATION.vertical_comparison
                        .location,
                    interaction_type: 'select',
                  });
                }}
              >
                {secondaryButtonText}
              </Button>
            </div>
          </FrontPageDeEmphasizeSecondaryCtaExperiment>
        </Box>
      </Box>
    </Box>
  );
};

VerticalComparisonTab.propTypes = {
  id: PropTypes.string,
  subtitle: PropTypes.string,
  description: PropTypes.string,
  primaryButtonText: PropTypes.string,
  primaryButtonUrl: PropTypes.string,
  secondaryButtonText: PropTypes.string,
  secondaryButtonUrl: PropTypes.string,
  abTestOverrides: PropTypes.shape({
    testName: PropTypes.string,
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        variantName: PropTypes.string,
        url: PropTypes.string,
        params: PropTypes.shape({
          trk_ab_test: PropTypes.string,
          trk_ab_test_variant: PropTypes.string,
        }),
      })
    ),
  }),
};

PropTypes.defaultProps = {
  abTestOverrides: {
    testName: '',
    variants: [
      {
        variantName: '',
        url: '',
        params: {
          trk_ab_test: '',
          trk_ab_test_variant: '',
        },
      },
    ],
  },
};

VerticalComparisonTab.displayName = 'VerticalComparisonTab';

export default VerticalComparisonTab;
